import React from "react"
import Link from "next/link"
import styled from "styled-components"
import { useSelector } from "react-redux"
import { selectAuthorization } from "~/slices/authorizationSlice"
import AcIcon from "~/components/atoms/AcIcon"
import ContentInner from "~/components/atoms/ContentInner"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CompanyType } from "~/model/Company"
import { useRouter } from "next/router"
import { decode } from "jsonwebtoken"
import { GUEST_TYPE } from "~/model/Workflow"

export default function Header() {
  const authorization = useSelector(selectAuthorization)
  const companyType = authorization.user?.company?.type
  const isBusinessCompany = companyType === CompanyType.BUSINESS
  const isOwnerAgencyCompany = companyType === CompanyType.OWNER_AGENCY
  const isCollectionAgency = companyType === CompanyType.COLLECTION_AGENCY
  const isCorporateHousing = companyType === CompanyType.CORPORATE_HOUSING
  const isFundCompany = companyType === CompanyType.FUND

  const getContractId = (storageKey) => {
    const contractIdJson = sessionStorage.getItem(storageKey)
    return contractIdJson && contractIdJson !== "undefined" ? JSON.parse(contractIdJson) : ""
  }

  let contractId = ""
  if (isBusinessCompany) {
    contractId = getContractId("businessContractId")
  } else if (isCorporateHousing) {
    contractId = getContractId("corporateContractId")
  }

  const router = useRouter()
  const currentPath = router.asPath
  const checkHiddenLink =
    currentPath.includes("resident/contract/") || currentPath.includes("signdocuments")

  let companyLogoLink = "/"
  let companyLabel = companyType as String
  let myPageHref = "/mypage"

  switch (companyType) {
    case CompanyType.BUSINESS:
      companyLogoLink = contractId ? `/business/contract/${contractId}` : "/business/mypage"
      myPageHref = "/business/mypage"
      break
    case CompanyType.OWNER_AGENCY:
      companyLogoLink = "/owner"
      companyLabel = "貸主/貸主代理"
      myPageHref = "/owner/mypage"
      break
    case CompanyType.COLLECTION_AGENCY:
      companyLogoLink = "/collector"
      companyLabel = "貸主（集金代行）"
      myPageHref = "/collector/mypage"
      break
    case CompanyType.CORPORATE_HOUSING:
      companyLogoLink = contractId ? `/corporate/contract/${contractId}` : "/corporate/mypage"
      companyLabel = "法人（社宅代行）"
      myPageHref = "/corporate/mypage"
      break
    case CompanyType.FUND:
      companyLogoLink = "/masterlease"
      companyLabel = ""
      myPageHref = "/masterlease/mypage"
      break
    default:
      break
  }

  return (
    <GlobalHeader className={"site-header"} checkHiddenLink={checkHiddenLink}>
      <ContentInner>
        <h1 className={"site-logo"}>
          {checkHiddenLink ? (
            <img
              src={"/assets/img/logo.png"}
              width={210}
              height={33}
              alt={"AMBITION Cloud 電子契約"}
            />
          ) : (
            <Link href={companyLogoLink} passHref>
              <img
                src={"/assets/img/logo.png"}
                width={210}
                height={33}
                alt={"AMBITION Cloud 電子契約"}
              />
            </Link>
          )}
        </h1>
        {authorization.user && (
          <InfoItems className={"info-items"}>
            <li className={"info-item"}>
              <a
                href={"https://sites.google.com/am-bition.jp/ambition-sign-faq/home"}
                target={"_blank"}
                rel={"noreferrer"}
              >
                <AcIcon className={"question-icon"} name={"question"} />
              </a>
            </li>
            <span className="company-type mx-3">{companyLabel}</span>
            {(authorization.user.roleId === 10 || authorization.user.roleId === 1) && (
              <li className={"info-item"}>
                <Link href={{ pathname: "/management/accounts/" }} passHref>
                  <span className={"user-meta me-1"}>管理画面</span>
                </Link>
                {!authorization.isRegisterTemplateForm && (
                  <FontAwesomeIcon icon="exclamation-circle" color="#ff0000" />
                )}
              </li>
            )}
            {isBusinessCompany && (
              <li className="info-item">
                <Link href={{ pathname: "/management/business/" }} passHref>
                  <span className="user-meta">管理画面</span>
                </Link>
              </li>
            )}
            {isOwnerAgencyCompany && (
              <li className="info-item">
                <Link href={{ pathname: "/management/owner-agency/" }} passHref>
                  <span className="user-meta">管理画面</span>
                </Link>
              </li>
            )}
            {isCollectionAgency && (
              <li className="info-item">
                <Link href={{ pathname: "/management/collector-agency/" }} passHref>
                  <span className="user-meta">管理画面</span>
                </Link>
              </li>
            )}
            {isCorporateHousing && (
              <li className="info-item">
                <Link href={{ pathname: "/management/corporate/" }} passHref>
                  <span className="user-meta">管理画面</span>
                </Link>
              </li>
            )}
            {isFundCompany && (
              <li className="info-item">
                <Link href={{ pathname: "/management/masterlease/" }} passHref>
                  <span className="user-meta">管理画面</span>
                </Link>
              </li>
            )}
            <li className={"info-item"}>
              <Link href={`${myPageHref}`} passHref>
                <span className={"full-name ms-2"}>{authorization.user.fullName}</span>
              </Link>
            </li>
            <li className={"info-item"}>
              <AcIcon name={"user-circle"} className={"user-icon"} />
            </li>
          </InfoItems>
        )}
      </ContentInner>
    </GlobalHeader>
  )
}

const InfoItems = styled.ul`
  display: flex;
  align-items: center;
  margin-bottom: 0;

  .info-item:last-child {
    margin-left: 24px;
  }

  .question-icon svg {
    color: #8f97a6;
    background-color: #ffffff;
    font-size: 11px;
    border-radius: 16px;
    border: 1px solid #d3d7de;
    width: 11px;
    padding: 5.5px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .info-item {
    display: flex;
    align-items: center;
  }

  .info-item span {
    color: #6966ea;
    cursor: pointer;
  }

  .info-item .user-icon {
    font-size: 33px;
    color: #e1e3ff;
  }

  @media screen and (max-width: 550px) {
    .user-meta {
      font-size: 12px;
      margin-left: 10px;
      margin-right: 0px;
    }
    .info-item .full-name,
    .company-type {
      display: none;
    }
    .info-item:last-child {
      margin-left: 10px;
    }
  }

  @media screen and (max-width: 370px) {
    display: none;
  }
`
const GlobalHeader = styled.header`
  &.site-header {
    height: 60px;
    background-color: #fff;
    border-bottom: 1px solid #ddd;
    @media screen and (max-width: 550px) {
      padding: 0 15px;
    }
  }

  &.site-header > div {
    height: inherit;
    display: flex;
    -webkit-box-align: center;
    align-items: center;
    -webkit-box-pack: justify;
    justify-content: space-between;
  }

  .site-logo {
    padding-top: 12px;
    padding-bottom: 12px;
    cursor: pointer;
    cursor: ${({ checkHiddenLink }) => (checkHiddenLink ? "default" : "pointer")};
  }

  .site-logo img {
    width: 210px;
    height: auto;
  }

  h1 {
    font-size: 20px;
    font-weight: bold;
    text-align: center;
  }
`
